import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ProductPicker, SizeGrid } from "@/components/NewBatchDialog";
import { CURRENT_SEASON } from "@/lib/season";

export const PRIORITIES = {
  alta: { label: "Alta", cls: "border-rose-500/50 text-rose-400" },
  media: { label: "Media", cls: "border-amber-500/50 text-amber-400" },
  bassa: { label: "Bassa", cls: "border-border text-muted-foreground" },
};
const seasonRank = (s) => (s === CURRENT_SEASON ? 0 : 1);

export const useSortedProducts = (enabled) => {
  const { data: products = [] } = useQuery({ queryKey: ["products"], enabled, queryFn: async () => (await api.get("/products")).data });
  return useMemo(() => [...products].sort((a, b) => seasonRank(a.season) - seasonRank(b.season) || a.name.localeCompare(b.name)), [products]);
};

export const AddQueueDialog = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState({});
  const [form, setForm] = useState({ priority: "media", due_date: "", notes: "" });
  const products = useSortedProducts(open);
  const total = Object.values(qty).reduce((s, v) => s + (Number(v) || 0), 0);
  const reset = () => { setProduct(null); setQty({}); setForm({ priority: "media", due_date: "", notes: "" }); };
  const save = useMutation({
    mutationFn: async () => (await api.post("/cutting/queue", { product_id: product.id, ...form, due_date: form.due_date || null, notes: form.notes || null,
      sizes: Object.entries(qty).map(([size, v]) => ({ size, quantity: Number(v) || 0 })) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cutting"] }); toast.success("Aggiunto alla coda di taglio"); setOpen(false); reset(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const submit = () => {
    if (!product) { toast.error("Seleziona un articolo"); return; }
    if (!total) { toast.error("Inserisci i pezzi da tagliare"); return; }
    save.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button data-testid="cutting-add-queue-btn"><Plus className="mr-2 h-4 w-4" />Da tagliare</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader><DialogTitle>Aggiungi articolo da tagliare</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <ProductPicker products={products} selected={product} onSelect={(p) => { setProduct(p); setQty({}); }} />
          {product && (
            <div className="space-y-1.5">
              <div className="flex justify-between"><Label>Pezzi da tagliare per taglia</Label><span className="text-xs text-muted-foreground">Totale: {num(total)} pz</span></div>
              <SizeGrid sizes={(product.sizes || []).map((s) => s.size)} values={qty} onChange={setQty} testid="queue-size" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Urgenza</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="queue-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITIES).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Consegna (facoltativa)</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="queue-due-date" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="queue-notes" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={save.isPending} data-testid="queue-save-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Aggiungi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
